import React, { useState, useEffect } from "react";
import { Plus, Search, Trash2, Check, X } from "lucide-react";
import { db } from "../firebase/config";
import {
	collection,
	getDocs,
	addDoc,
	updateDoc,
	deleteDoc,
	doc,
} from "firebase/firestore";

const UserManagement = () => {
	const [users, setUsers] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [showAddModal, setShowAddModal] = useState(false);
	const [newUser, setNewUser] = useState({
		name: "",
		email: "",
		hasAccess: false,
	});
	const [loading, setLoading] = useState(true);

	// Load users from Firestore
	useEffect(() => {
		loadUsers();
	}, []);

	const loadUsers = async () => {
		try {
			const querySnapshot = await getDocs(collection(db, "users"));
			const usersData = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setUsers(usersData);
		} catch (error) {
			console.error("Error loading users:", error);
			// Fallback to mock data if Firebase not configured
			setUsers([
				{
					id: "1",
					name: "John Doe",
					email: "john@example.com",
					hasAccess: true,
				},
				{
					id: "2",
					name: "Jane Smith",
					email: "jane@example.com",
					hasAccess: false,
				},
			]);
		} finally {
			setLoading(false);
		}
	};

	const addUser = async () => {
		if (!newUser.name || !newUser.email) return;

		try {
			const docRef = await addDoc(collection(db, "users"), {
				name: newUser.name,
				email: newUser.email,
				hasAccess: newUser.hasAccess,
				createdAt: new Date(),
			});
			setUsers([...users, { id: docRef.id, ...newUser }]);
		} catch (error) {
			console.error("Error adding user:", error);
			// Fallback for demo
			setUsers([...users, { id: Date.now().toString(), ...newUser }]);
		}

		setNewUser({ name: "", email: "", hasAccess: false });
		setShowAddModal(false);
	};

	const toggleAccess = async (userId) => {
		const user = users.find((u) => u.id === userId);
		if (!user) return;

		try {
			await updateDoc(doc(db, "users", userId), {
				hasAccess: !user.hasAccess,
			});
		} catch (error) {
			console.error("Error updating user:", error);
		}

		setUsers(
			users.map((u) =>
				u.id === userId ? { ...u, hasAccess: !u.hasAccess } : u
			)
		);
	};

	const deleteUser = async (userId) => {
		if (!window.confirm("Are you sure you want to delete this user?")) return;

		try {
			await deleteDoc(doc(db, "users", userId));
		} catch (error) {
			console.error("Error deleting user:", error);
		}

		setUsers(users.filter((u) => u.id !== userId));
	};

	const filteredUsers = users.filter(
		(user) =>
			user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.email.toLowerCase().includes(searchTerm.toLowerCase())
	);

	if (loading) {
		return <div className="text-white">Loading users...</div>;
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-3xl font-bold text-white mb-2">
						User Management
					</h2>
					<p className="text-gray-400">
						Manage user access to your portfolio content.
					</p>
				</div>
				<button
					onClick={() => setShowAddModal(true)}
					className="bg-gradient-to-r from-orange-700 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-orange-800 hover:to-orange-600 transition-all flex items-center space-x-2"
				>
					<Plus className="w-5 h-5" />
					<span>Add User</span>
				</button>
			</div>

			{/* Search Bar */}
			<div className="relative">
				<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
				<input
					type="text"
					placeholder="Search users..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
				/>
			</div>

			{/* Users Table */}
			<div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
				<table className="w-full">
					<thead className="bg-gray-700">
						<tr>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
								Name
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
								Email
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
								Access
							</th>
							<th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-700">
						{filteredUsers.map((user) => (
							<tr key={user.id} className="hover:bg-gray-750">
								<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
									{user.name}
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
									{user.email}
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<span
										className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
											user.hasAccess
												? "bg-green-900 text-green-200"
												: "bg-red-900 text-red-200"
										}`}
									>
										{user.hasAccess ? "Granted" : "Denied"}
									</span>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
									<button
										onClick={() => toggleAccess(user.id)}
										className={`inline-flex items-center px-3 py-1 rounded ${
											user.hasAccess
												? "bg-red-600 hover:bg-red-700 text-white"
												: "bg-green-600 hover:bg-green-700 text-white"
										}`}
									>
										{user.hasAccess ? (
											<X className="w-4 h-4 mr-1" />
										) : (
											<Check className="w-4 h-4 mr-1" />
										)}
										{user.hasAccess ? "Revoke" : "Grant"}
									</button>
									<button
										onClick={() => deleteUser(user.id)}
										className="inline-flex items-center px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white"
									>
										<Trash2 className="w-4 h-4" />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Add User Modal */}
			{showAddModal && (
				<div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
					<div className="bg-gray-800 border border-gray-700 rounded-xl p-8 max-w-md w-full mx-4">
						<h3 className="text-2xl font-bold text-white mb-6">Add New User</h3>
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium mb-2 text-gray-300">
									Name
								</label>
								<input
									type="text"
									value={newUser.name}
									onChange={(e) =>
										setNewUser({ ...newUser, name: e.target.value })
									}
									className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
									placeholder="Enter name"
								/>
							</div>
							<div>
								<label className="block text-sm font-medium mb-2 text-gray-300">
									Email
								</label>
								<input
									type="email"
									value={newUser.email}
									onChange={(e) =>
										setNewUser({ ...newUser, email: e.target.value })
									}
									className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
									placeholder="Enter email"
								/>
							</div>
							<div className="flex items-center">
								<input
									type="checkbox"
									id="hasAccess"
									checked={newUser.hasAccess}
									onChange={(e) =>
										setNewUser({ ...newUser, hasAccess: e.target.checked })
									}
									className="w-4 h-4 text-orange-600 bg-gray-900 border-gray-600 rounded focus:ring-orange-500"
								/>
								<label
									htmlFor="hasAccess"
									className="ml-2 text-sm text-gray-300"
								>
									Grant access to premium content
								</label>
							</div>
							<div className="flex space-x-3 pt-4">
								<button
									onClick={addUser}
									className="flex-1 bg-gradient-to-r from-orange-700 to-orange-500 text-white py-3 rounded-lg hover:from-orange-800 hover:to-orange-600 transition-all"
								>
									Add User
								</button>
								<button
									onClick={() => setShowAddModal(false)}
									className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-all"
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default UserManagement;
